import React, { useState, useEffect, useMemo } from 'react';
import exaniModulosService from '../services/exaniModulosService';
import MathGraph from './MathGraph';
import { QuestionText, OptionText, isImageDataOrUrl } from './MathRenderer';
import '../styles/BancoModulosExani.css';
import '../styles/MathGraph.css';

const BancoModulosExani = () => {
  const [modulos, setModulos] = useState([]);
  const [carreras, setCarreras] = useState([]);
  const [selectedCarrera, setSelectedCarrera] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estado de la práctica interactiva
  const [activeModulo, setActiveModulo] = useState(null);
  const [preguntas, setPreguntas] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isAnswerConfirmed, setIsAnswerConfirmed] = useState(false);
  const [score, setScore] = useState(0);
  const [isQuizFinished, setIsQuizFinished] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [modulosData, carrerasData] = await Promise.all([
        exaniModulosService.getModulos(),
        exaniModulosService.getCarreras()
      ]);
      setModulos(modulosData);
      setCarreras(carrerasData);

      if (!exaniModulosService.isPreloadComplete()) {
        preloadInBackground();
      }
    } catch (err) {
      console.error('Error cargando datos de banco EXANI-II:', err);
      setError('No se pudieron cargar los módulos desde la base de datos.');
    } finally {
      setLoading(false);
    }
  };

  const preloadInBackground = async () => {
    try {
      await exaniModulosService.validateCache();

      const modulosData = await exaniModulosService.getModulos(true);

      await exaniModulosService.preloadPreguntasModulos(modulosData);
    } catch (err) {
      console.warn('Pre-cargado en background falló:', err);
    }
  };

  // Filtrado de módulos según carrera y búsqueda
  const filteredModulos = useMemo(() => {
    return modulos.filter(mod => {
      const matchesSearch = !searchTerm ||
        mod.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mod.area.toLowerCase().includes(searchTerm.toLowerCase());

      const carreraObj = carreras.find(c => c.slug === selectedCarrera);
      const matchesCarrera = !selectedCarrera || (carreraObj && carreraObj.modulos_requeridos?.some(req =>
        mod.nombre.toLowerCase().includes(req.toLowerCase()) ||
        mod.moduloKey.toLowerCase().includes(req.toLowerCase().replace(/ /g, '_'))
      ));

      return matchesSearch && matchesCarrera;
    });
  }, [modulos, carreras, selectedCarrera, searchTerm]);

  // Iniciar práctica de un módulo
  const handleStartPractica = async (modulo) => {
    try {
      setActiveModulo(modulo);
      setLoadingQuestions(true);
      setCurrentIndex(0);
      setSelectedOption(null);
      setIsAnswerConfirmed(false);
      setScore(0);
      setIsQuizFinished(false);

      const qs = await exaniModulosService.getPreguntasModulo(modulo.moduloKey);
      setPreguntas(qs);
    } catch (err) {
      console.error('Error al cargar preguntas:', err);
      alert('Error al cargar las preguntas de este módulo.');
      setActiveModulo(null);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleSelectOption = (index) => {
    if (!isAnswerConfirmed) {
      setSelectedOption(index);
    }
  };

  const handleConfirmAnswer = () => {
    if (selectedOption === null) return;
    setIsAnswerConfirmed(true);
    const currentQ = preguntas[currentIndex];
    const op = currentQ?.opciones[selectedOption];
    const letters = ['A', 'B', 'C', 'D'];
    const isCorrect = op?.correcta === true || op?.correcta === 'true' || currentQ?.correcta === selectedOption || currentQ?.correcta === letters[selectedOption];
    if (isCorrect) {
      setScore(prev => prev + 1);
    }
  };

  const handleNextQuestion = () => {
    if (currentIndex + 1 < preguntas.length) {
      setCurrentIndex(prev => prev + 1);
      setSelectedOption(null);
      setIsAnswerConfirmed(false);
    } else {
      setIsQuizFinished(true);
    }
  };

  const handlePrevQuestion = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setSelectedOption(null);
      setIsAnswerConfirmed(false);
    }
  };

  const handleExitPractica = () => {
    setActiveModulo(null);
    setPreguntas([]);
  };

  // RENDER: Pantalla de Resultados
  if (isQuizFinished && activeModulo) {
    const total = preguntas.length;
    const porcentaje = total > 0 ? Math.round((score * 100) / total) : 0;

    return (
      <div className="banco-exani-container">
        <div className="banco-results-card">
          <h2 style={{ fontFamily: 'var(--font-tactical)', color: 'var(--gold-matte)' }}>
            ¡MÓDULO COMPLETADO!
          </h2>
          <p style={{ color: 'var(--text-secondary)' }}>{activeModulo.nombre}</p>

          <div className="banco-results-percentage" style={{
            color: porcentaje >= 70 ? '#4caf50' : '#ffb300'
          }}>
            {porcentaje}%
          </div>

          <p style={{ fontSize: '1.1rem', marginBottom: '24px' }}>
            Aciertos: <strong>{score}</strong> de <strong>{total}</strong> reactivos
          </p>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button
              className="banco-btn-back"
              onClick={() => handleStartPractica(activeModulo)}
            >
              🔄 Reintentar
            </button>
            <button
              className="banco-btn-primary"
              onClick={handleExitPractica}
            >
              Finalizar Práctica
            </button>
          </div>
        </div>
      </div>
    );
  }

  // RENDER: Modo Práctica de Preguntas
  if (activeModulo) {
    const currentQ = preguntas[currentIndex];

    return (
      <div className="banco-exani-container">
        <div className="banco-practica-header">
          <button className="banco-btn-back" onClick={handleExitPractica}>
            ← Volver al Catálogo
          </button>
          <div>
            <h3 style={{ margin: 0, color: 'var(--gold-matte)', fontSize: '1.2rem' }}>
              {activeModulo.nombre}
            </h3>
            {preguntas.length > 0 && (
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                Reactivo {currentIndex + 1} de {preguntas.length}
              </span>
            )}
          </div>
        </div>

        {loadingQuestions ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <p>Cargando reactivos de CENEVAL...</p>
          </div>
        ) : !currentQ ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <p>No se encontraron preguntas en este módulo.</p>
            <button className="banco-btn-primary" onClick={handleExitPractica}>Volver</button>
          </div>
        ) : (
          <div>
            {/* Barra de progreso superior */}
            <div className="banco-progress-bar-bg" style={{ marginBottom: '20px', height: '8px' }}>
              <div
                className="banco-progress-bar-fill"
                style={{ width: `${((currentIndex + 1) / preguntas.length) * 100}%` }}
              />
            </div>

            {/* Tarjeta de la Pregunta */}
            <div className="banco-question-card">
              <div className="banco-question-tag">
                REACTIVO #{currentIndex + 1} · {currentQ.area || activeModulo.area}
              </div>
              <div className="banco-question-text">
                <QuestionText text={currentQ.pregunta} />
              </div>
              {currentQ.grafica && (
                <div style={{ marginTop: '16px' }}>
                  <MathGraph config={currentQ.grafica} />
                </div>
              )}
            </div>

            {/* Opciones */}
            <div className="banco-options-list">
              {currentQ.opciones.map((op, idx) => {
                const letters = ['A', 'B', 'C', 'D'];
                const isSelected = selectedOption === idx;
                const opContent = op.texto || op.respuesta || op.imagen || op.url || (typeof op === 'string' ? op : '');
                const hasImg = isImageDataOrUrl(opContent);
                const isOpCorrect = op.correcta === true || op.correcta === 'true' || currentQ.correcta === idx || currentQ.correcta === letters[idx];
                let statusClass = '';

                if (isAnswerConfirmed) {
                  if (isOpCorrect) {
                    statusClass = 'correct';
                  } else if (isSelected && !isOpCorrect) {
                    statusClass = 'incorrect';
                  }
                } else if (isSelected) {
                  statusClass = 'selected';
                }

                return (
                  <div
                    key={idx}
                    className={`banco-option-item ${statusClass} ${hasImg ? 'has-image' : ''} ${isAnswerConfirmed ? 'disabled' : ''}`}
                    onClick={() => handleSelectOption(idx)}
                  >
                    <div className="banco-option-letter">{letters[idx] || idx + 1}</div>
                    <div className="banco-option-content" style={{ flex: 1, minWidth: 0 }}>
                      <OptionText text={opContent} />
                    </div>
                    {isAnswerConfirmed && isOpCorrect && <span className="banco-check-icon">✅</span>}
                    {isAnswerConfirmed && isSelected && !isOpCorrect && <span className="banco-check-icon">❌</span>}
                  </div>
                );
              })}
            </div>

            {/* Retroalimentación Pedagógica CENEVAL */}
            {isAnswerConfirmed && currentQ.feedback && (
              <div className="banco-feedback-box">
                <span style={{ fontSize: '1.4rem' }}>💡</span>
                <div className="banco-feedback-content">
                  <h4>Explicación Oficial</h4>
                  <QuestionText text={currentQ.feedback} />
                </div>
              </div>
            )}

            {/* Barra de Acciones */}
            <div className="banco-actions-bar">
              <button
                className="banco-btn-back"
                disabled={currentIndex === 0}
                onClick={handlePrevQuestion}
                style={{ visibility: currentIndex === 0 ? 'hidden' : 'visible' }}
              >
                ← Anterior
              </button>

              {!isAnswerConfirmed ? (
                <button
                  className="banco-btn-primary"
                  disabled={selectedOption === null}
                  onClick={handleConfirmAnswer}
                >
                  Confirmar Respuesta
                </button>
              ) : (
                <button
                  className="banco-btn-primary"
                  onClick={handleNextQuestion}
                >
                  {currentIndex + 1 < preguntas.length ? 'Siguiente Reactivo →' : 'Ver Resultados Finales'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // RENDER: Catálogo Principal de Módulos
  return (
    <div className="banco-exani-container">
      <div className="banco-exani-header">
        <h1 className="banco-exani-title">
          <span>🎯</span> Banco EXANI-II
        </h1>
        <p className="banco-exani-subtitle">
          22 Módulos Oficiales CENEVAL · Práctica con reactivos validados y retroalimentación interactiva
        </p>
      </div>

      {/* Controles: Filtrar por carrera y búsqueda */}
      <div className="banco-controls-panel">
        <div className="banco-control-group">
          <label className="banco-control-label">Filtrar por Carrera Universitaria</label>
          <select
            className="banco-select"
            value={selectedCarrera}
            onChange={(e) => setSelectedCarrera(e.target.value)}
          >
            <option value="">Todas las Carreras (22 módulos)</option>
            {carreras.map(c => (
              <option key={c.slug} value={c.slug}>
                {c.nombre} ({c.modulos_requeridos?.length || 2} módulos requeridos)
              </option>
            ))}
          </select>
        </div>

        <div className="banco-control-group">
          <label className="banco-control-label">Buscar Módulo</label>
          <input
            type="text"
            className="banco-search-input"
            placeholder="Ej. Cálculo, Medicina, Filosofía..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Grid de Módulos */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <p>Cargando banco de módulos desde la base de datos...</p>
        </div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '50px 0', color: 'var(--error-color)' }}>
          <p>{error}</p>
          <button className="banco-btn-primary" onClick={loadData}>Reintentar</button>
        </div>
      ) : (
        <>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '14px' }}>
            Mostrando <strong>{filteredModulos.length}</strong> de <strong>{modulos.length}</strong> módulos disponibles
          </p>
          <div className="banco-modulos-grid">
            {filteredModulos.map(mod => {
              const progressPct = Math.min(100, Math.round((mod.total / (mod.objetivo || 150)) * 100));

              return (
                <div key={mod.moduloKey} className="banco-modulo-card">
                  <div>
                    <div className="banco-modulo-header">
                      <h3 className="banco-modulo-title">{mod.nombre}</h3>
                      {mod.area && (
                        <span className="banco-modulo-area-badge">{mod.area}</span>
                      )}
                    </div>

                    <div className="banco-modulo-stats">
                      <div className="banco-modulo-count-row">
                        <span>Preguntas disponibles</span>
                        <strong>{mod.total} / {mod.objetivo || 150}</strong>
                      </div>
                      <div className="banco-progress-bar-bg">
                        <div
                          className="banco-progress-bar-fill"
                          style={{
                            width: `${progressPct}%`,
                            background: mod.total >= 150 ? '#4caf50' : 'var(--gold-matte)'
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    className="banco-btn-practicar"
                    onClick={() => handleStartPractica(mod)}
                  >
                    <span>▶</span> Practicar Módulo
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default BancoModulosExani;

